import { api, ApiError, get, post } from '@/lib/api';
import { useAuthStore } from '@/features/auth/auth.store';
import type {
  Anexo,
  ArvoreDeChamado,
  CategoriaDeStatus,
  Chamado,
  Comentario,
  Escalonamento,
  Lancamento,
  Nivel,
  PaginaDeChamados,
  RelogioDeSla,
  ResumoDeTempo,
  TipoDeChamado,
  VeredictoDeFechamento,
} from '@/features/tickets/types';

/**
 * Acesso à API de chamados.
 *
 * Uma função por rota do `TicketsController`, sem nenhuma inventada: o que não
 * existe no controller não existe aqui, e a tela que precisaria disso mostra a
 * lacuna em vez de simular o dado.
 */

const RAIZ = '/tickets';

export interface FiltrosDeChamado {
  status?: CategoriaDeStatus[];
  type?: TipoDeChamado[];
  q?: string;
  rootsOnly?: boolean;
  openOnly?: boolean;
  assigneeId?: string;
  teamId?: string;
  requesterId?: string;
  organizationId?: string;
  limit?: number;
}

type ValorDeConsulta = string | number | boolean | readonly string[] | undefined;

function comConsulta(caminho: string, parametros: Record<string, ValorDeConsulta>): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor === undefined || valor === '') continue;
    // Array vira parâmetro repetido — é o formato que o `@Transform` do DTO
    // espera, e um valor único chega como escalar sem quebrar a validação.
    if (Array.isArray(valor)) {
      for (const item of valor) busca.append(chave, item);
    } else {
      busca.append(chave, String(valor));
    }
  }
  const consulta = busca.toString();
  return consulta ? `${caminho}?${consulta}` : caminho;
}

// ── chamados ───────────────────────────────────────────────────────────────

export function listarChamados(
  filtros: FiltrosDeChamado,
  cursor?: string,
): Promise<PaginaDeChamados> {
  return get<PaginaDeChamados>(
    comConsulta(RAIZ, {
      ...filtros,
      // O `openOnly` sobrescreve o filtro de categoria no servidor; mandar os
      // dois juntos daria a impressão de que o de status foi ignorado por bug.
      status: filtros.openOnly ? undefined : filtros.status,
      cursor,
    }),
  );
}

export function obterChamado(id: string): Promise<Chamado> {
  return get<Chamado>(`${RAIZ}/${id}`);
}

export function obterArvore(id: string): Promise<ArvoreDeChamado> {
  return get<ArvoreDeChamado>(`${RAIZ}/${id}/tree`);
}

export function obterFilhos(id: string): Promise<Chamado[]> {
  return get<Chamado[]>(`${RAIZ}/${id}/children`);
}

export function verificarFechamento(id: string): Promise<VeredictoDeFechamento> {
  return get<VeredictoDeFechamento>(`${RAIZ}/${id}/can-close`);
}

export interface NovoChamado {
  type: TipoDeChamado;
  subject: string;
  descriptionHtml?: string;
  requesterId: string;
  organizationId: string;
  categoryId?: string;
  subcategoryId?: string;
  impact?: Nivel;
  urgency?: Nivel;
}

export function criarChamado(dados: NovoChamado): Promise<Chamado> {
  return post<Chamado>(RAIZ, dados);
}

export interface NovoSubchamado {
  subject: string;
  type?: TipoDeChamado;
  descriptionHtml?: string;
  /** Sem categoria, o filho herda a do pai (requisito 4). */
  categoryId?: string;
  subcategoryId?: string;
  impact?: Nivel;
  urgency?: Nivel;
}

export function criarSubchamado(paiId: string, dados: NovoSubchamado): Promise<Chamado> {
  return post<Chamado>(`${RAIZ}/${paiId}/children`, dados);
}

// ── estado e atribuição ────────────────────────────────────────────────────

/**
 * Muda a situação do chamado.
 *
 * Recebe a **chave** do status (`statusKey`), não o `id`: é o que o
 * `ChangeStatusDto` espera. Os dois vêm juntos no `TicketStatusRef` e não se
 * substituem — o `id` resolve o `statusId` que o chamado carrega, a chave
 * identifica o destino.
 */
export function mudarSituacao(
  id: string,
  dados: { statusKey: string; note?: string },
): Promise<Chamado> {
  return post<Chamado>(`${RAIZ}/${id}/status`, dados);
}

/**
 * Define o responsável.
 *
 * Vai pelo `PATCH` geral porque não existe rota própria de atribuição, e leva
 * a `version` que a tela leu: sem ela, duas pessoas atribuindo ao mesmo tempo
 * fariam a última gravação vencer em silêncio; com ela, a segunda recebe 409 e
 * a tela avisa.
 *
 * Não há como **desatribuir** por aqui: o DTO valida `assigneeId` como uuid, e
 * `null` seria recusado com 400. A tela não oferece a opção em vez de oferecer
 * um botão que sempre falha.
 */
export function atribuirResponsavel(
  id: string,
  assigneeId: string,
  version: number,
): Promise<Chamado> {
  return api<Chamado>(`${RAIZ}/${id}`, { method: 'PATCH', body: { assigneeId, version } });
}

export interface AtualizacaoDeChamado {
  teamId?: string | null;
  categoryId?: string | null;
  subcategoryId?: string | null;
  serviceId?: string | null;
  impact?: Nivel;
  urgency?: Nivel;
  version: number;
}

export function atualizarChamado(id: string, dados: AtualizacaoDeChamado): Promise<Chamado> {
  return api<Chamado>(`${RAIZ}/${id}`, { method: 'PATCH', body: dados });
}

// ── SLA ────────────────────────────────────────────────────────────────────

export function obterSla(id: string): Promise<RelogioDeSla[]> {
  return get<RelogioDeSla[]>(`${RAIZ}/${id}/sla`);
}

// ── comentários ────────────────────────────────────────────────────────────

export function listarComentarios(id: string): Promise<Comentario[]> {
  return get<Comentario[]>(`${RAIZ}/${id}/comments`);
}

export function comentar(
  id: string,
  dados: { bodyHtml: string; visibility: 'PUBLIC' | 'INTERNAL' },
): Promise<Comentario> {
  return post<Comentario>(`${RAIZ}/${id}/comments`, dados);
}

// ── tempo trabalhado ───────────────────────────────────────────────────────

export function listarLancamentos(id: string): Promise<Lancamento[]> {
  return get<Lancamento[]>(`${RAIZ}/${id}/worklogs`);
}

export function obterResumoDeTempo(id: string): Promise<ResumoDeTempo> {
  return get<ResumoDeTempo>(`${RAIZ}/${id}/worklogs/summary`);
}

export function lancarTempo(
  id: string,
  dados: { startedAt: string; minutes: number; billable: boolean; note?: string },
): Promise<Lancamento> {
  return post<Lancamento>(`${RAIZ}/${id}/worklogs`, dados);
}

// ── anexos ─────────────────────────────────────────────────────────────────

export function listarAnexos(id: string): Promise<Anexo[]> {
  return get<Anexo[]>(`${RAIZ}/${id}/attachments`);
}

/**
 * Envio e download passam por `fetch` cru, não pelo `api()`.
 *
 * O cliente HTTP serializa o corpo como JSON e interpreta a resposta como
 * JSON — as duas coisas erradas aqui: o upload é `multipart/form-data` e o
 * download é um fluxo binário. O que se repete é só o cabeçalho de
 * autenticação e a renovação no 401.
 */
async function requisicaoBruta(caminho: string, init: RequestInit): Promise<Response> {
  const enviar = async (): Promise<Response> => {
    const token = useAuthStore.getState().accessToken;
    return fetch(`/api/v1${caminho}`, {
      ...init,
      headers: { ...init.headers, ...(token ? { authorization: `Bearer ${token}` } : {}) },
    });
  };

  let resposta = await enviar();
  if (resposta.status === 401 && (await useAuthStore.getState().refresh())) {
    resposta = await enviar();
  }
  if (!resposta.ok) {
    throw new ApiError(resposta.status, `Falha na requisição (${resposta.status})`);
  }
  return resposta;
}

export async function enviarAnexo(ticketId: string, arquivo: File): Promise<Anexo> {
  const formulario = new FormData();
  formulario.append('file', arquivo, arquivo.name);
  const resposta = await requisicaoBruta(`${RAIZ}/${ticketId}/attachments`, {
    method: 'POST',
    body: formulario,
  });
  return (await resposta.json()) as Anexo;
}

/**
 * Baixa o anexo por blob em vez de apontar um `<a href>` para a rota.
 *
 * A rota exige `Authorization`, que uma navegação do navegador não envia — o
 * link direto voltaria 401. O `URL.revokeObjectURL` fecha o vazamento de
 * memória que sobra quando o blob não é liberado.
 */
export async function baixarAnexo(ticketId: string, anexo: Anexo): Promise<void> {
  const resposta = await requisicaoBruta(`${RAIZ}/${ticketId}/attachments/${anexo.id}/content`, {
    method: 'GET',
  });
  const url = URL.createObjectURL(await resposta.blob());
  try {
    const ancora = document.createElement('a');
    ancora.href = url;
    ancora.download = anexo.fileName;
    ancora.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ── escalonamento ──────────────────────────────────────────────────────────

export function listarEscalonamentos(id: string): Promise<Escalonamento[]> {
  return get<Escalonamento[]>(`${RAIZ}/${id}/escalations`);
}

export function escalonar(
  id: string,
  dados: { reason?: string; toTeamId?: string },
): Promise<void> {
  return api<void>(`${RAIZ}/${id}/escalate`, { method: 'POST', body: dados });
}

// ── Fase 9 — sugestões de IA ─────────────────────────────────────────────

export interface SugestaoDeIa {
  articles: Array<{ id: string; code: string; title: string; summary: string; score: number }>;
  llm?: {
    suggestedCategory: string | null;
    suggestedPriority: string | null;
    suggestedType: string | null;
    rationale: string;
  };
}

export function obterSugestoesIa(id: string): Promise<SugestaoDeIa> {
  return get<SugestaoDeIa>(`${RAIZ}/${id}/ai-suggest`);
}

// ── chaves do TanStack Query ───────────────────────────────────────────────

export const chaves = {
  raiz: ['chamados'] as const,
  lista: (filtros: FiltrosDeChamado) => ['chamados', 'lista', filtros] as const,
  item: (id: string) => ['chamados', 'item', id] as const,
  arvore: (id: string) => ['chamados', 'item', id, 'arvore'] as const,
  fechamento: (id: string) => ['chamados', 'item', id, 'fechamento'] as const,
  sla: (id: string) => ['chamados', 'item', id, 'sla'] as const,
  comentarios: (id: string) => ['chamados', 'item', id, 'comentarios'] as const,
  lancamentos: (id: string) => ['chamados', 'item', id, 'lancamentos'] as const,
  tempo: (id: string) => ['chamados', 'item', id, 'tempo'] as const,
  anexos: (id: string) => ['chamados', 'item', id, 'anexos'] as const,
  escalonamentos: (id: string) => ['chamados', 'item', id, 'escalonamentos'] as const,
  sugestoesIa: (id: string) => ['chamados', 'item', id, 'ai-suggest'] as const,
};
